import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldCheck, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { rupee2 } from "../lib/format";
import { api, apiErr } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Checkout() {
  const navigate = useNavigate();
  const { grouped, totals, items, table, clear } = useCart();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [idemKey] = useState(() => `chk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (totals.count === 0 && !paid) navigate("/order");
  }, [totals.count, paid, navigate]);

  if (totals.count === 0 && !paid) return null;

  const pay = async () => {
    if (!/^\d{10}$/.test(mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    try {
      const payload = {
        table_code: table.table_code,
        customer: { name: name || "Guest", mobile },
        idempotency_key: idemKey,
        items: items.map((l) => ({
          item_id: l.item_id, restaurant_id: l.restaurant_id, restaurant_name: l.restaurant_name,
          group_id: l.group_id, group_name: l.group_name, name: l.name, price: l.price,
          qty: l.qty, variant: l.variant, addons: l.addons, food_type: l.food_type, prep_time: l.prep_time,
        })),
      };
      const { data } = await api.post("/public/checkout", payload);
      const master = data.order;

      if (data.gateway.simulated) {
        await finalize(master.id, {});
      } else {
        openRazorpay(data, master);
      }
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
    }
  };

  const finalize = async (masterId, rzp) => {
    try {
      const { data } = await api.post("/public/payments/verify", {
        master_order_id: masterId,
        razorpay_order_id: rzp.razorpay_order_id,
        razorpay_payment_id: rzp.razorpay_payment_id,
        razorpay_signature: rzp.razorpay_signature,
      });
      setPaid(true);
      toast.success("Payment successful! Order confirmed.");
      navigate(`/track/${data.order.id}`, { replace: true });
      clear();
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
    }
  };

  const openRazorpay = (data, master) => {
    const options = {
      key: data.gateway.key_id,
      amount: data.amount_paise,
      currency: "INR",
      name: "Omaxe Food Courts",
      description: `Order ${master.order_number}`,
      order_id: data.gateway.order_id,
      prefill: { name: name || "Guest", contact: mobile },
      theme: { color: "#D4AF37" },
      handler: (resp) => finalize(master.id, resp),
      modal: { ondismiss: () => setLoading(false) },
    };
    if (!window.Razorpay) { toast.error("Payment SDK not loaded"); setLoading(false); return; }
    new window.Razorpay(options).open();
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08183A] pb-40">
      <div className="sticky top-0 z-50 glass px-4 py-3 flex items-center gap-2">
        <button onClick={() => navigate("/cart")} className="h-8 w-8 rounded-full card-surface-2 flex items-center justify-center" data-testid="checkout-back-btn"><ChevronLeft size={18} /></button>
        <div className="font-display font-bold text-lg">Checkout</div>
      </div>

      <div className="p-4 space-y-4">
        <div className="card-surface rounded-2xl p-4">
          <div className="text-xs text-amber-400 uppercase tracking-wide font-semibold mb-3">Your details</div>
          <label className="text-xs text-slate-400">Name (optional)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="w-full bg-[#16336B] rounded-xl px-3 py-2.5 text-sm mt-1 mb-3 border hairline focus:border-amber-500 outline-none" data-testid="checkout-name" />
          <label className="text-xs text-slate-400">Mobile number *</label>
          <div className="relative mt-1">
            <Smartphone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit mobile" inputMode="numeric"
              className="w-full bg-[#16336B] rounded-xl pl-9 pr-3 py-2.5 text-sm border hairline focus:border-amber-500 outline-none" data-testid="checkout-mobile" />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Order updates & digital bill are sent to WhatsApp.</p>
        </div>

        <div className="card-surface rounded-2xl p-4">
          <div className="text-xs text-amber-400 uppercase tracking-wide font-semibold mb-3">Order summary</div>
          {grouped.map((r) => (
            <div key={r.restaurant_id} className="flex justify-between text-sm py-1">
              <span className="text-slate-300">{r.restaurant_name} <span className="text-slate-500">×{r.lines.reduce((s, l) => s + l.qty, 0)}</span></span>
              <span>{rupee2(r.subtotal)}</span>
            </div>
          ))}
          <div className="border-t hairline mt-2 pt-2 space-y-1 text-sm">
            <div className="flex justify-between text-slate-400"><span>Taxes + Packaging + Fees</span><span>{rupee2(totals.tax + totals.packaging + totals.convenience)}</span></div>
            <div className="flex justify-between font-display font-extrabold text-lg"><span>Payable</span><span className="gold-text">{rupee2(totals.payable)}</span></div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <ShieldCheck size={15} className="text-emerald-400" />
          Secure payment · idempotent · one master order per payment
        </div>
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md p-4 glass">
        <button onClick={pay} disabled={loading} className="gold-btn w-full rounded-xl py-3.5 flex items-center justify-center gap-2 text-base disabled:opacity-60" data-testid="checkout-pay-button">
          {loading ? <><Loader2 className="animate-spin" size={18} /> Processing…</> : <>Pay {rupee2(totals.payable)}</>}
        </button>
      </div>
    </div>
  );
}
